import React, { useState, useEffect, useRef } from "react";
import Multiselect from "multiselect-react-dropdown";
import CustomArrow from "../utils/CustomStyleComponent/ArrowComponent";
import CustomCrossButton from "../utils/CustomStyleComponent/CrossButtonComponent";
import Select from "../components/CustomSelect";
import { TOptionType } from "../types/CustomSelect";
import clsx from "clsx";
import "../styles/multiselectStyles.css";

import { ProductSearch } from '../generatedRequest/product';
import { getAccessory } from "../generatedRequest/accessory";

interface Region {
    regionName: string;
    regionUrl: string;
    language: string[];
    createdBy: string;
    regionalContacts?: any[];
}

interface Category {
    categoryName: string;
    categoryId?: string;
    id?: string;
    [key: string]: any;
}

interface CompatibleFormProps {
    regions: Region[];
    categories: Category[];
    onDataChange?: (data: {
        selectedAccessory: any;
        selectedProduct: any;
        selectedCategories: Category[];
        selectedRegion?: any
    }) => void;
    resetForm?: boolean;
}

const CompatibleForm: React.FC<CompatibleFormProps> = ({ 
    regions = [], 
    categories = [],
    onDataChange,
    resetForm
}) => {

    const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

    // Product Search States
    const [productSearch, setProductSearch] = useState("");
    const [productSuggestions, setProductSuggestions] = useState<any[]>([]);
    const [showProductSuggestions, setShowProductSuggestions] = useState(false);
    const [isProductLoading, setIsProductLoading] = useState(false);
    const [productCurrentPage, setProductCurrentPage] = useState(1);
    const [hasMoreProductResults, setHasMoreProductResults] = useState(false);
    const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
    const [lastProductSearchQuery, setLastProductSearchQuery] = useState("");
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [isProductIdSearch, setIsProductIdSearch] = useState(false);

    // Accessory Search States
    const [modelNumber, setModelNumber] = useState("");
    const [accessorySuggestions, setAccessorySuggestions] = useState<any[]>([]);
    const [showAccessorySuggestions, setShowAccessorySuggestions] = useState(false);
    const [isAccessoryLoading, setIsAccessoryLoading] = useState(false);
    const [accessoryCurrentPage, setAccessoryCurrentPage] = useState(1);
    const [hasMoreAccessoryResults, setHasMoreAccessoryResults] = useState(false);
    const [loadingMoreAccessories, setLoadingMoreAccessories] = useState(false);
    const [lastAccessorySearchQuery, setLastAccessorySearchQuery] = useState("");
    const [selectedAccessory, setSelectedAccessory] = useState<any>(null);
    const [showRegionError, setShowRegionError] = useState(false);

    const productSearchContainerRef = useRef<HTMLDivElement | null>(null);
    const accessorySearchContainerRef = useRef<HTMLDivElement | null>(null);

    // Validation: Check if region and subcategory are selected
    const isSearchEnabled = selectedRegions.length > 0;

    useEffect(() => {
        // Reset form state when resetForm prop is true (parent controls the flag)
        if (resetForm) {
            console.log("Resetting CompatibleForm state");
            setSelectedAccessory(null);
            setSelectedProduct(null);
            setSelectedRegions([]);
            setSelectedCategories([]);
            setProductSearch("");
            setModelNumber("");
            // Note: resetForm is a prop; the parent component should clear it if needed.
        }   
    }, [resetForm]);

    // Notify parent component when data changes
    useEffect(() => {
    if (onDataChange) {
        onDataChange({
            selectedAccessory,
            selectedProduct,
            selectedCategories,
            selectedRegion: selectedRegions[0] || null
        });
    }
}, [selectedAccessory, selectedProduct, selectedCategories, selectedRegions]);
    // Click outside handler for product search
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                productSearchContainerRef.current &&
                productSearchContainerRef.current.contains &&
                !productSearchContainerRef.current.contains(event.target)
            ) {
                setShowProductSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Click outside handler for accessory search
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                accessorySearchContainerRef.current &&
                accessorySearchContainerRef.current.contains &&
                !accessorySearchContainerRef.current.contains(event.target)
            ) {
                setShowAccessorySuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Helper function to check if string is a valid ObjectId
    const isValidObjectId = (str: string): boolean => {
        return /^[0-9a-fA-F]{24}$/.test(str);
    };

    // Helper function to filter accessories by selected regions
    const filterAccessoriesByRegions = (accessories: any[]) => {
        if (selectedRegions.length === 0) {
            return accessories;
        }

        const selectedRegionNames = selectedRegions.map(region => region.regionName);

        return accessories.filter(accessory => {
            if (!accessory.regions || !Array.isArray(accessory.regions)) {
                return false;
            }

            return accessory.regions.some((region: any) => {
                const regionName = region.regionName || region.name;
                return selectedRegionNames.includes(regionName);
            });
        });
    };

    // Product Search API call
    const searchProductsFromAPI = async (search: string, page: number = 1, append: boolean = false) => {
        if (!search || search.trim() === '') {
            setProductSuggestions([]);
            setShowProductSuggestions(false);
            return;
        }

        try {
            if (page === 1) {
                setIsProductLoading(true);
            } else {
                setLoadingMoreProducts(true);
            }

            const trimmedQuery = search.trim();
            setLastProductSearchQuery(trimmedQuery);

            // Check if searching by Product ID
            const isIdSearch = isValidObjectId(trimmedQuery);
            setIsProductIdSearch(isIdSearch);
            const response = await ProductSearch(`search=${trimmedQuery}&page=${page}`);

            if (response && response.status === 200) {
                let newResults: any[] = [];
                let hasMore = false;

                if (response.data.data.data) {
                    if (Array.isArray(response.data.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    } else if (response.data.data.data.results && Array.isArray(response.data.data.data.results)) {
                        newResults = response.data.data.data.results;
                        hasMore = response.data.data.data.hasMore ||
                            (response.data.data.data.total_pages && page < response.data.data.data.total_pages) ||
                            newResults.length === 10;
                    } else if (response.data.data.data && Array.isArray(response.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    }

                    // For Product ID search, there should be only one result or none
                    if (isIdSearch && newResults.length > 0) {
                        // Only show the exact match
                        hasMore = false;
                    }

                    setHasMoreProductResults(hasMore);

                    if (append) {
                        setProductSuggestions(prev => [...prev, ...newResults]);
                    } else {
                        setProductSuggestions(newResults);
                    }

                    setShowProductSuggestions(newResults.length > 0);

                    // Auto-select if only one result from Product ID search
                    if (isIdSearch && newResults.length === 1 && !append) {
                        handleSelectProduct(newResults[0]);
                    }
                } else {
                    if (!append) {
                        setProductSuggestions([]);
                        setShowProductSuggestions(false);
                    }
                    setHasMoreProductResults(false);
                }
            } else {
                if (!append) {
                    setProductSuggestions([]);
                    setShowProductSuggestions(false);
                }
                setHasMoreProductResults(false);
            }
        } catch (error) {
            if (!append) {
                setProductSuggestions([]);
                setShowProductSuggestions(false);
            }
            setHasMoreProductResults(false);
        } finally {
            setIsProductLoading(false);
            setLoadingMoreProducts(false);
        }
    };

    // Accessory Search API call with region filtering
    const searchAccessoriesFromAPI = async (search: string, page: number = 1, append: boolean = false) => {
        if (!search || search.trim() === '') {
            setAccessorySuggestions([]);
            setShowAccessorySuggestions(false);
            return;
        }

        try {
            if (page === 1) {
                setIsAccessoryLoading(true);
            } else {
                setLoadingMoreAccessories(true);
            }

            const trimmedQuery = search.trim();
            setLastAccessorySearchQuery(trimmedQuery);

            const response = await getAccessory(`search=${trimmedQuery}&page=${page}`);

            if (response && response.status === 200) {
                let newResults: any[] = [];
                let hasMore = false;

                if (response.data.data.data) {
                    if (Array.isArray(response.data.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    } else if (response.data.data.data.results && Array.isArray(response.data.data.data.results)) {
                        newResults = response.data.data.data.results;
                        hasMore = response.data.data.data.hasMore ||
                            (response.data.data.data.total_pages && page < response.data.data.data.total_pages) ||
                            newResults.length === 10;
                    } else if (response.data.data.data && Array.isArray(response.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    }

                    // Apply region filtering
                    const filteredResults = filterAccessoriesByRegions(newResults);
                    setHasMoreAccessoryResults(hasMore);

                    if (append) {
                        setAccessorySuggestions(prev => [...prev, ...filteredResults]);
                    } else {
                        setAccessorySuggestions(filteredResults);
                    }

                    // Show error if no results after filtering and regions are selected
                    if (selectedRegions.length > 0 && filteredResults.length === 0 && newResults.length > 0) {
                        setShowRegionError(true);
                    } else {
                        setShowRegionError(false);
                    }

                    setShowAccessorySuggestions(filteredResults.length > 0);
                } else {
                    if (!append) {
                        setAccessorySuggestions([]);
                        setShowAccessorySuggestions(false);
                    }
                    setHasMoreAccessoryResults(false);
                }
            } else {
                if (!append) {
                    setAccessorySuggestions([]);
                    setShowAccessorySuggestions(false);
                }
                setHasMoreAccessoryResults(false);
            }
        } catch (error) {
            console.error("Error searching accessories:", error);
            if (!append) {
                setAccessorySuggestions([]);
                setShowAccessorySuggestions(false);
            }
            setHasMoreAccessoryResults(false);
        } finally {
            setIsAccessoryLoading(false);
            setLoadingMoreAccessories(false);
        }
    };

    const handleProductSearch = () => {
        if (!isSearchEnabled) return;
        setProductCurrentPage(1);
        searchProductsFromAPI(productSearch);
    };

    const handleAccessorySearch = () => {
        if (!isSearchEnabled) return;
        setAccessoryCurrentPage(1);
        setShowRegionError(false);
        searchAccessoriesFromAPI(modelNumber);
    };

    const loadMoreProductResults = () => {
        const nextPage = productCurrentPage + 1;
        setProductCurrentPage(nextPage);
        searchProductsFromAPI(lastProductSearchQuery, nextPage, true);
    };

    const loadMoreAccessoryResults = () => {
        const nextPage = accessoryCurrentPage + 1;
        setAccessoryCurrentPage(nextPage);
        searchAccessoriesFromAPI(lastAccessorySearchQuery, nextPage, true);
    };

    const handleProductSearchChange = (e: any) => {
        const value = e.target.value;
        setProductSearch(value);
        setSelectedProduct(null);
        setIsProductIdSearch(false);
    };

    const handleAccessorySearchChange = (e: any) => {
        const value = e.target.value;
        setModelNumber(value);
        setSelectedAccessory(null);
        setShowRegionError(false);
    };

    const handleProductKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!isSearchEnabled) return;
            setProductCurrentPage(1);
            searchProductsFromAPI(productSearch);
        }
    };

    const handleAccessoryKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!isSearchEnabled) return;
            setAccessoryCurrentPage(1);
            setShowRegionError(false);
            searchAccessoriesFromAPI(modelNumber);
        }
    };

    const handleSelectProduct = (product: any) => {
        const productName = product.displayName || product.productName;
        setProductSearch(productName);
        setSelectedProduct(product);
        setShowProductSuggestions(false);
        setIsProductIdSearch(false);
    };

    const handleSelectAccessory = (accessory: any) => {
        const accessoryName = accessory.accessoryName || accessory.modelNumber || accessory.displayName || accessory.name;
        setModelNumber(accessoryName);
        setSelectedAccessory(accessory);
        setShowAccessorySuggestions(false);
    };

    // Ensure regions is always an array and filter out invalid entries
    const regionsList = React.useMemo(() => {
        if (!regions) {
            return [];
        }
        if (!Array.isArray(regions)) {
            return [];
        }

        const filtered = regions.filter(region => {
            const isValid = region &&
                typeof region === 'object' &&
                region.regionName;
            return isValid;
        });
        return filtered;
    }, [regions]);

    // Ensure categories is always an array and filter out invalid entries
    const categoriesList = React.useMemo(() => {
        if (!categories) {
            return [];
        }
        if (!Array.isArray(categories)) {
            return [];
        }

        const filtered = categories
            .filter(category => {
                const categoryName = category?.subcategoryName || category?.categoryName;
                if(!["docking station","usb-hub"].includes(categoryName?.toLowerCase())) {
                    return false;
                }
                const hasName = category &&
                    typeof category === 'object' &&
                    categoryName &&
                    typeof categoryName === 'string' &&
                    categoryName.trim() !== '';

                if (!hasName) {
                    console.log("Invalid category (missing name):", category);
                }
                return hasName;
            })
            .map(category => ({
                ...category,
                categoryName: category.subcategoryName || category.categoryName
            }));
        return filtered;
    }, [categories]);

    const onRegionSelect = (selectedList: Region[], selectedItem: Region) => {
        setSelectedRegions(selectedList);
        
        // Re-filter current accessory results if there are any
        if (accessorySuggestions.length > 0) {
            const filteredResults = filterAccessoriesByRegions(accessorySuggestions);
            setAccessorySuggestions(filteredResults);
        }
    };

    const onRegionRemove = (selectedList: Region[], removedItem: Region) => {
        setSelectedRegions(selectedList);
        
        // Re-filter current accessory results if there are any
        if (accessorySuggestions.length > 0) {
            const filteredResults = filterAccessoriesByRegions(accessorySuggestions);
            setAccessorySuggestions(filteredResults);
        }
    };

    const onCategorySelect = (selectedList: Category[], selectedItem: Category) => {
        setSelectedCategories(selectedList);
    };

    const onCategoryRemove = (selectedList: Category[], removedItem: Category) => {
        setSelectedCategories(selectedList);
    };

    return (
        <div className="w-100 w-md-100 pt-5" id="kt_login_signin_form">
            <div className="d-flex flex-column flex-md-row mb-3 w-100 gap-5">
                {/* Regions Multiselect */}
                <div className="d-flex flex-column flex-md-column w-md-50 w-100">
                    <label className="form-label fs-6 fw-bolder text-dark">
                        Regions <span className="text-danger">*</span>
                    </label>
                    <div style={{ zIndex: 10 }}>
                        {regionsList.length > 0 ? (
                            <Multiselect
                                className="multiselect-container multiselect-search-box"
                                options={regionsList}
                                avoidHighlightFirstOption={true}
                                displayValue="regionName"
                                showArrow={true}
                                customArrow={<CustomArrow />}
                                customCloseIcon={<CustomCrossButton />}
                                selectedValues={selectedRegions}
                                placeholder="Select Regions"
                                onSelect={onRegionSelect}
                                onRemove={onRegionRemove}
                                keepSearchTerm={true}
                                showCheckbox={false}
                                hidePlaceholder={false}
                                selectionLimit={1}
                                
                            />
                        ) : (
                            <div className="form-control text-muted">
                                No regions available
                            </div>
                        )}
                    </div>
                </div>

                {/* Product Search */}
                <div className="d-flex flex-column flex-md-column w-md-50 w-100">
                    <label className="form-label fs-6 fw-bolder text-dark">
                        Product Search
                    </label>
                  
                    <div className="position-relative" ref={productSearchContainerRef}>
                        <div className="input-group">
                            <input
                                value={productSearch}
                                onChange={handleProductSearchChange}
                                onKeyPress={handleProductKeyPress}
                                disabled={!isSearchEnabled || isProductLoading}
                                className={clsx("form-control bg-transparent")}
                                type="text"
                                placeholder="Enter product name or Product ID (e.g., 6720e96fc43e14fb1d0768c0)"
                                onFocus={() => {
                                    if (productSuggestions.length > 0) {
                                        setShowProductSuggestions(true);
                                    }
                                }}
                            />
                            <button
                                className="btn btn-primary"
                                onClick={handleProductSearch}
                                disabled={!isSearchEnabled || isProductLoading}
                                type="button"
                            >
                                {isProductLoading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    "Search"
                                )}
                            </button>
                        </div>
                        {/* Product Suggestions Dropdown */}
                        {showProductSuggestions && productSuggestions.length > 0 && (
                            <div
                                className="position-absolute w-100 bg-white border border-top-0 shadow-lg rounded-bottom-2 overflow-hidden"
                                style={{ top: '100%', zIndex: 1050, maxHeight: '250px' }}
                            >
                                <div className="overflow-auto" style={{ maxHeight: '200px' }}>
                                    {productSuggestions.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className="px-3 py-2 cursor-pointer border-bottom border-light hover-bg-light d-flex flex-column"
                                            style={{ transition: 'background-color 0.15s ease-in-out', minHeight: '40px' }}
                                            onClick={() => handleSelectProduct(suggestion)}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            {suggestion.displayName || suggestion.productName || JSON.stringify(suggestion)}
                                        </div>
                                    ))}
                                </div>
                                {hasMoreProductResults && !isProductIdSearch && (
                                    <div className="border-top bg-light">
                                        <button
                                            className="btn btn-link text-primary w-100 py-2 text-decoration-none fw-medium"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                loadMoreProductResults();
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            disabled={loadingMoreProducts}
                                            style={{ fontSize: '0.875rem' }}
                                        >
                                            {loadingMoreProducts ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Loading more results...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-chevron-down me-2"></i>
                                                    Show more results
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Category Multiselect */}
               {/*  <div className="d-flex flex-column flex-md-column w-md-50 w-100">
                    <label className="form-label fs-6 fw-bolder text-dark">
                        SubCategory <span className="text-danger">*</span>
                    </label>
                    <div style={{ zIndex: 10 }}>
                        {categoriesList.length > 0 ? (
                            <Multiselect
                                className="multiselect-container multiselect-search-box"
                                options={categoriesList}
                                avoidHighlightFirstOption={true}
                                displayValue="categoryName"
                                showArrow={true}
                                customArrow={<CustomArrow />}
                                customCloseIcon={<CustomCrossButton />}
                                selectedValues={selectedCategories}
                                placeholder="Select Categories"
                                onSelect={onCategorySelect}
                                onRemove={onCategoryRemove}
                                keepSearchTerm={true}
                                showCheckbox={false}
                                hidePlaceholder={false}
                            />
                        ) : (
                            <div className="form-control text-muted">
                                No categories available
                            </div>
                        )}
                    </div>
                </div> */}
            </div>
            <div className="d-flex flex-column flex-md-row mb-3 w-100 gap-5">
                

                {/* Accessory Model Number Search */}
                <div className="d-flex flex-column flex-md-column w-md-50 w-100">
                    <label className="form-label fs-6 fw-bolder text-dark">
                        Accessory ModelNumber
                    </label>
                    <div className="position-relative" ref={accessorySearchContainerRef}>
                        <div className="input-group">
                            <input
                                value={modelNumber}
                                onChange={handleAccessorySearchChange}
                                onKeyPress={handleAccessoryKeyPress}
                                disabled={!isSearchEnabled || isAccessoryLoading}
                                className={clsx("form-control bg-transparent")}
                                type="text"
                                placeholder="Enter model number and press Enter to search"
                                onFocus={() => {
                                    if (accessorySuggestions.length > 0) {
                                        setShowAccessorySuggestions(true);
                                    }
                                }}
                            />
                            <button
                                className="btn btn-primary"
                                onClick={handleAccessorySearch}
                                disabled={!isSearchEnabled || isAccessoryLoading}
                                type="button"
                            >
                                {isAccessoryLoading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    "Search"
                                )}
                            </button>
                        </div>

                        {/* Accessory Suggestions Dropdown */}
                        {showAccessorySuggestions && accessorySuggestions.length > 0 && (
                            <div
                                className="position-absolute w-100 bg-white border border-top-0 shadow-lg rounded-bottom-2 overflow-hidden"
                                style={{ top: '100%', zIndex: 1050, maxHeight: '250px' }}
                            >
                                <div className="overflow-auto" style={{ maxHeight: '200px' }}>
                                    {accessorySuggestions.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className="px-3 py-2 cursor-pointer border-bottom border-light hover-bg-light d-flex align-items-center"
                                            style={{ transition: 'background-color 0.15s ease-in-out', minHeight: '40px' }}
                                            onClick={() => handleSelectAccessory(suggestion)}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <span className="text-truncate">
                                                {suggestion.accessoryName+' ('+suggestion.modelNumber+')'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {hasMoreAccessoryResults && (
                                    <div className="border-top bg-light">
                                        <button
                                            className="btn btn-link text-primary w-100 py-2 text-decoration-none fw-medium"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                loadMoreAccessoryResults();
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            disabled={loadingMoreAccessories}
                                            style={{ fontSize: '0.875rem' }}
                                        >
                                            {loadingMoreAccessories ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Loading more results...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-chevron-down me-2"></i>
                                                    Show more results
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* Region Error Message */}
                    {showRegionError && (
                        <div className="mt-2 mb-0 d-flex align-items-center text-danger">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            <span>This accessory is not available in the selected region(s). Please change the region selection.</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompatibleForm;